import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import TopNav from "./TopNav";
import { API_URL } from "./config";
import Swal from 'sweetalert2';

function PosHome() {
  const location = useLocation();
  const navigate = useNavigate();
  const { shiftDetails, userDetails = {}, companyBranchDetails } = location.state || {}; // Default userDetails to an empty object

  // Retrieve details from local storage
  const companyName = localStorage.getItem('companyName') || "REMS ANYTHING";
  const branchName = localStorage.getItem('branchName') || "Branch: ";
  const dateTimeOpen = shiftDetails ? shiftDetails.date_time_open : "Not available";
  const operatorName = userDetails.userName || "Unknown";
  const companyId = localStorage.getItem('company_id');
  const branchId = localStorage.getItem('branch_id');
  const shiftId = shiftDetails ? shiftDetails.shift_id : null; 

  // State for products and search
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedGoods, setSelectedGoods] = useState([]);
  const [quantity, setQuantity] = useState(1); // Quantity state
  const [amountGiven, setAmountGiven] = useState(0); // Amount given state
  const [change, setChange] = useState(0); // Change state
  const [loading, setLoading] = useState(false); // Loading state

  // Log all retrieved details
  console.log('Company Name:', companyName);
  console.log('Branch Name:', branchName);
  console.log('Shift Open Time:', dateTimeOpen);
  console.log('Operator:', operatorName);

  useEffect(() => {
    const fetchProducts = async () => {
      const url = `${API_URL}/productdefinition/full_products_definitions_by_company_id_branch_id/${companyId}/${branchId}`;
      
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const fetchedProducts = await response.json();
        setProducts(fetchedProducts); // Store fetched products in state
        console.log('Products:', fetchedProducts); // Log the products
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    
    fetchProducts(); // Call the function to fetch products
  }, [companyId, branchId]);

  useEffect(() => {
    if (searchTerm) {
      const results = products.filter(product =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.product_code.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 3); // Limit results to 3
      setFilteredProducts(results);
    } else {
      setFilteredProducts([]);
    }
  }, [searchTerm, products]);

  const handleProductSelect = (product) => {
    setSearchTerm(product.product_name); // Set the input to the selected product's name
    setQuantity(1); // Reset quantity to default
    setFilteredProducts([]); // Clear the suggestions immediately
  };

  const handleAddToSellList = () => {
    if (searchTerm) {
      const selectedProduct = products.find(product => product.product_name === searchTerm);
      if (quantity > 0) {
        const productToAdd = {
          ...selectedProduct,
          quantity: quantity,
          total: selectedProduct.selling_price * quantity // Calculate total for this product
        };
        setSelectedGoods(prev => [...prev, productToAdd]); // Add selected product to the list
        setSearchTerm(""); // Clear the input field
        setQuantity(1); // Reset quantity
      }
    }
  };

  const handleClearTransaction = () => {
    setSelectedGoods([]); // Clear the selected goods
    setAmountGiven(0); // Reset amount given
    setChange(0); // Reset change
  };

  const handleCompletePurchase = async () => {
    if (amountGiven <= 0) {
        Swal.fire("Error", "Please enter the amount given.", "error");
        return;
    }

    setLoading(true);

    const postBody = {
        propertyid: "",
        company_id: companyId,
        branch_id: branchId,
        project: "",
        daterec: new Date().toISOString().split('T')[0],
        timerec: new Date().toTimeString().split(' ')[0],
        datefor: new Date().toISOString().split('T')[0],
        description: "InStore POS Sale",
        quantity: 0,
        combined_receipt_total_quantity: selectedGoods.reduce((total, item) => total + item.quantity, 0),
        amntrec: amountGiven,
        amntrec_credit: 0.00,
        interest: 0.00,
        principal: 0.00,
        deposit: 0.00,
        balance: 0.00,
        receipt_ref_dispatch_locked: "UNLOCKED",
        feesdue: 0.00,
        username: operatorName,
        confirmed_by: "AUTO",
        authorized_by: "AUTO",
        branch: branchName,
        dispatch_status: "DISPATCHED",
        dispatch_by: "AUTO",
        dispatch_date: new Date().toISOString().split('T')[0],
        dispatch_time: new Date().toTimeString().split(' ')[0],
        dispatched_quantity: 0,
        undispatched_quantity_remaining: 0,
        undispatched_inventory_release_date: "0000-00-00",
        dispatch_sales_shift_id: shiftId,
        sales_shifts_id: shiftId,
        folio: "CAS",
        pmode: "CASH",
        currency: "USD",
        price: 0.00,
        usd_price: 0.00,
        usd_cost_per_unit: 0.00,
        credit_repayment_currency_price: 0.00,
        ref: "00000",
        vat_rate_charged: 0.15,
        vat_invoice_no: "00000",
        discount_requisition_id: "0",
        discount_amount: 0.00,
        discount_cost: 0.00,
        monthsbehind: 0,
        movementafterpayment: 0,
        suspencebal: 0.00,
        suspencedr: 0.00,
        suspencecr: 0.00,
        suspencenarration: "0.00",
        marketing_media_sale_origin: "Online",
        marketing_loyalty_structure_id: "00000",
        customerid: 1,
        comment: "00000",
        sync_status: "00000"
    };

    try {
        const response = await fetch(`${API_URL}/salepostrans/add_sale_pos_trans_sold`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(postBody),
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const result = await response.json();
        const currentTrans = result.result.insertId;

        // Generate two six-digit confirmation codes
        const generateConfirmationCode = () => Math.floor(100000 + Math.random() * 900000).toString();

        const driver_confirmation_code1 = generateConfirmationCode();
        const driver_confirmation_code2 = generateConfirmationCode();

        console.log("Driver Confirmation Code 1:", driver_confirmation_code1);
        console.log("Driver Confirmation Code 2:", driver_confirmation_code2);

        for (const product of selectedGoods) {
            const saleRecord = {
                company_id: companyId,
                branch_id: branchId,
                sale_records_id: currentTrans,
                product_id: product.product_id,
                quantity: product.quantity,
                unit_cost: product.unit_cost,
                selling_price: product.selling_price,
                driver_confirmation_code1, // Add confirmation codes
                driver_confirmation_code2  // Add confirmation codes
            };
            try {
                const saleResponse = await fetch(`${API_URL}/salelist/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(saleRecord),
                });
                if (!saleResponse.ok) {
                    throw new Error("Failed to post sale record");
                }
            } catch (error) {
                console.error("Error posting sale record:", error);
            }
        }

        for (const dispatchList of selectedGoods) {
            const dispatchBody = {
                company_id: companyId,
                branch_id: branchId,
                sale_records_id: currentTrans,
                product_id: dispatchList.product_id,
                quantity: dispatchList.quantity,
            };

            await fetch(`${API_URL}/inventorymgt/dispatch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(dispatchBody),
            });
        }

        // Navigate to Receipt
        navigate("/Receipt", {
            state: {
                paymentId: currentTrans,
                selectedGoods,
                totalAmount: selectedGoods.reduce((total, item) => total + item.total, 0),
                operatorName: operatorName,
                branchName: branchName,
                shiftId: shiftId,
            }
        });

        handleClearTransaction();
    } catch (error) {
        console.error("Error completing purchase:", error);
    }
    setLoading(false);
};

  useEffect(() => {
    const totalCost = selectedGoods.reduce((total, item) => total + item.total, 0);
    if (amountGiven) {
      setChange(amountGiven - totalCost); // Calculate change
    }
  }, [amountGiven, selectedGoods]);

  return (
    <div>
      <TopNav />
      <div className="container-fluid" style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        padding: "20px",  // Reduced padding
        marginTop: "30px", 
        width: "100%",
      }}>
        <header className="my-3 text-center">
          <h1 className="my-3" style={{ color: "#333", fontSize: "1.8rem" }}>Point of Sale System</h1> {/* Reduced font size */}
        </header>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Processing your transaction...</p>
          </div>
        )}

        <div className="row">
          <div className="col-md-8 col-sm-12">
            <div className="mb-3">
              <div className="input-group">
                <select className="form-control" style={{
                  borderRadius: 0,
                  backgroundColor: "#fff",
                  height: "40px",  // Reduced height
                }}>
                  <option value="">Select Customer</option>
                  {/* Map through customers here when integrated */}
                </select>

                <input
                  type="number"
                  className="form-control"
                  placeholder="Qty"
                  style={{
                    borderRadius: 0,
                    backgroundColor: "#fff",
                    width: "70px", // Reduced width
                    height: "40px", // Reduced height
                  }}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)} // Update quantity
                  min="1" // Minimum quantity
                />

                <input
                  type="text"
                  className="form-control"
                  placeholder="Scan or type item name"
                  style={{
                    borderRadius: 0,
                    backgroundColor: "#fff",
                    height: "40px",  // Reduced height
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} // Update search term
                />

                <div className="input-group-append">
                  <span className="input-group-text me-2" style={{
                    borderRadius: 0,
                    backgroundColor: "#fff",
                    height: "40px", // Reduced height
                  }}>
                    <FaSearch />
                  </span>
                </div>

                <button 
                  className="btn btn-primary px-2" 
                  style={{ height: "40px", fontSize: "1rem" }}
                  onClick={handleAddToSellList} // Add product to sell list
                >
                  Add
                </button>
              </div>

              {/* Suggestions list */}
              {filteredProducts.length > 0 && searchTerm && (
                <ul className="list-group">
                  {filteredProducts.map(product => (
                    <li
                      key={product.product_id}
                      className="list-group-item list-group-item-action"
                      onClick={() => handleProductSelect(product)} // Select product when clicked
                    >
                      {product.product_name} - {product.product_code}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="mb-3" style={{
                height: "200px",  // Reduced height
                overflowY: "scroll",
                border: "1px solid #ccc",
                padding: "10px",
                textAlign: "left",
              }}>
                <h4 style={{ color: "#333", fontSize: "1.2rem" }}>Selected Goods</h4> {/* Reduced font size */}
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Product ID</th>
                      <th>Product Code</th>
                      <th>Description</th>
                      <th>Unit Cost</th>
                      <th>Quantity</th>
                      <th>VAT</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGoods.map((item, index) => (
                      <tr key={index}>
                        <td>{item.product_id}</td>
                        <td>{item.product_code}</td>
                        <td>{item.product_name}</td>
                        <td>{item.selling_price}</td>
                        <td>{item.quantity}</td> {/* Display quantity */}
                        <td>{0 /* Placeholder for VAT, can be modified later */}</td>
                        <td>{item.total.toFixed(2)}</td> {/* Display total for this product */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-md-4 col-sm-12">
            <div>
              <div className="row mb-3">
                <h4 style={{
                  fontSize: "1.5rem", // Reduced font size
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#333",
                }}>
                  Total Cost: {selectedGoods.reduce((total, item) => total + item.total, 0).toFixed(2)} USD
                </h4>
              </div>

              <div className="row mb-3">
                <div className="col">
                  <label style={{ fontSize: "1rem" }}>Choose Currency</label>
                  <select className="form-control" style={{ borderRadius: 0 }}>
                    <option value="USD">USD</option>
                    <option value="ZIG">ZIG</option>
                    <option value="RAND">RAND</option>
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col">
                  <label style={{ fontSize: "1rem" }}>Amount Given</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    style={{ borderRadius: 0 }} 
                    value={amountGiven}
                    onChange={(e) => setAmountGiven(e.target.value)} // Update amount given
                  />
                </div>
                <div className="col">
                  <label style={{ fontSize: "1rem" }}>Change</label>
                  <input type="text" className="form-control" value={change.toFixed(2)} readOnly style={{ borderRadius: 0 }} />
                </div>
              </div>

              <div className="d-flex justify-content-center mt-3">
                <button 
                  className="btn btn-success me-2" 
                  style={{ fontSize: "0.9rem" }} 
                  onClick={handleCompletePurchase} // Call complete purchase
                >
                  Complete Purchase
                </button>
                <button 
                  className="btn btn-primary" 
                  style={{ fontSize: "0.9rem" }} 
                  onClick={handleClearTransaction} // Clear transaction
                >
                  Clear Transaction
                </button>
               
              </div>
            </div>
          </div>
        </div>

        <footer className="mt-4 text-center" style={{ fontSize: "0.9rem" }}> {/* Reduced font size */}
          <div className="row">
            <div className="col">
              <p style={{ color: "#333" }}>Company Name: {companyName}</p>
            </div>
            <div className="col">
              <p style={{ color: "#333" }}>Branch: {branchName}</p>
            </div>
            <div className="col">
              <p style={{ color: "#333" }}>Shift ID: {shiftDetails ? shiftDetails.shift_id : "No active shift"}</p>
            </div>
            <div className="col">
              <p style={{ color: "#333" }}>Shift Open Time: {dateTimeOpen}</p>
            </div>
            <div className="col">
              <p style={{ color: "#333" }}>Till Operator: {operatorName}</p>
            </div>
          </div>

          <hr />

          <div className="row justify-content-center">
            <div className="col-auto">
              <button className="btn btn-danger me-2">
                <Link to="/inventory" style={{ color: "inherit", textDecoration: "none" }}>
                  &nbsp; POS &nbsp;
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/PettyCash" style={{ color: "inherit", textDecoration: "none" }}>
                  Petty Cash
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/inventory" style={{ color: "inherit", textDecoration: "none" }}>
                  Inventory
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/openShift" style={{ color: "inherit", textDecoration: "none" }}>
                Open Shift
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/EndShift" style={{ color: "inherit", textDecoration: "none" }}>
                  End Shift
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/ControlPanel" style={{ color: "inherit", textDecoration: "none" }}>
                  Control Panel
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/Reports" style={{ color: "inherit", textDecoration: "none" }}>
                  Reports
                </Link>
              </button>
              <button className="btn btn-danger me-2">
                <Link to="/DailySalesReport" style={{ color: "inherit", textDecoration: "none" }}>
                  Daily Sale Reports
                </Link>
              </button>
              <button 
                  className="btn btn-danger me-2" 
                  style={{ fontSize: "0.9rem" }} 
                  onClick={() => navigate(`/CloseShift/${shiftId}`)} 
                >
                  Close Shift
                </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PosHome;